import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import enTranslation from './locales/en/translation.json';
import elTranslation from './locales/el/translation.json';
import ruTranslation from './locales/ru/translation.json';
import heTranslation from './locales/he/translation.json';

const resources = {
  en: { translation: enTranslation },
  el: { translation: elTranslation },
  ru: { translation: ruTranslation },
  he: { translation: heTranslation },
};

// Restore saved language preference
const savedLang = localStorage.getItem('msk-language') ?? 'en';

void i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: savedLang,
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false, // React handles XSS
    },
    react: {
      useSuspense: false,
    },
  });

export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', dir: 'ltr' },
  { code: 'el', name: 'Ελληνικά', dir: 'ltr' },
  { code: 'ru', name: 'Русский', dir: 'ltr' },
  { code: 'he', name: 'עברית', dir: 'rtl' },
] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]['code'];

/**
 * Wave 2 W2.8 — Hebrew RTL pass.
 *
 * Apply the active language's text direction + lang code to the
 * `<html>` element so flex/grid/text-align rules and native form
 * controls mirror correctly for Hebrew. Settings sections, GP-letter
 * editor, and patient-report preview all inherit the cascade with no
 * per-component changes required.
 *
 * The function is invoked once at module load (for the persisted
 * locale) and again on every `languageChanged` event.
 */
function applyHtmlLangAndDir(lng: string): void {
  const entry = SUPPORTED_LANGUAGES.find((l) => l.code === lng);
  const dir = entry?.dir ?? 'ltr';
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.lang = lng;
    document.documentElement.dir = dir;
  }
}

// Initial application — runs before React mounts so the first paint is
// already in the correct direction.
applyHtmlLangAndDir(savedLang);

// Persist language changes to localStorage + reapply lang/dir.
i18n.on('languageChanged', (lng: string) => {
  localStorage.setItem('msk-language', lng);
  applyHtmlLangAndDir(lng);
});

export default i18n;
